/* Todaysunny hero video: play only the visible responsive source and respect user intent. */
(function initializeTodaysunnyVideo(root, factory) {
  const api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = { shouldPlay: api.shouldPlay };
  }

  if (root && root.document) {
    api.mount();
  }
})(typeof window !== 'undefined' ? window : globalThis, function createTodaysunnyVideo(root) {
  function shouldPlay(state) {
    return Boolean(
      state.active
      && state.visible
      && !state.documentHidden
      && state.autoplay
      && !state.manuallyPaused
      && !state.reducedMotion,
    );
  }

  if (!root || !root.document) {
    return { shouldPlay, mount() {} };
  }

  const document = root.document;
  const controllers = new Map();
  const reduced = root.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = root.matchMedia('(max-width: 749px)');

  function addMediaListener(query, listener) {
    if (query.addEventListener) query.addEventListener('change', listener);
    else query.addListener(listener);
  }

  function removeMediaListener(query, listener) {
    if (query.removeEventListener) query.removeEventListener('change', listener);
    else query.removeListener(listener);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-ts-video]').forEach((wrapper) => {
      if (controllers.has(wrapper)) return;

      const video = wrapper.querySelector('video');
      const toggle = wrapper.querySelector('[data-ts-video-toggle]');
      const label = wrapper.querySelector('[data-ts-video-label]');
      if (!video) return;

      const state = {
        visible: false,
        manuallyPaused: false,
        suppressPause: false,
      };

      const isActiveSource = () => (
        wrapper.classList.contains('ts-hero-film--mobile') === mobile.matches
      );

      const updateToggle = () => {
        const playing = !video.paused && !video.ended;
        const nextAction = playing ? 'Pause brand video' : 'Play brand video';
        wrapper.dataset.playing = String(playing);
        if (toggle) toggle.setAttribute('aria-label', nextAction);
        if (label) label.textContent = nextAction;
      };

      const pauseInternally = () => {
        if (video.paused) {
          updateToggle();
          return;
        }
        state.suppressPause = true;
        video.pause();
      };

      const sync = () => {
        const play = shouldPlay({
          active: isActiveSource(),
          visible: state.visible,
          documentHidden: document.hidden,
          autoplay: wrapper.dataset.autoplay === 'true',
          manuallyPaused: state.manuallyPaused,
          reducedMotion: reduced.matches,
        });

        if (!play) {
          pauseInternally();
          return;
        }

        video.play().then(updateToggle).catch(updateToggle);
      };

      const onPause = () => {
        if (state.suppressPause) state.suppressPause = false;
        else state.manuallyPaused = true;
        updateToggle();
      };

      const onPlay = () => {
        state.manuallyPaused = false;
        updateToggle();
      };

      const onToggle = () => {
        if (video.paused || video.ended) {
          state.manuallyPaused = false;
          video.play().then(updateToggle).catch(updateToggle);
        } else {
          state.manuallyPaused = true;
          video.pause();
        }
      };

      video.addEventListener('pause', onPause);
      video.addEventListener('play', onPlay);
      video.addEventListener('ended', updateToggle);
      if (toggle) toggle.addEventListener('click', onToggle);

      let observer;
      if ('IntersectionObserver' in root) {
        observer = new root.IntersectionObserver(([entry]) => {
          state.visible = entry.isIntersecting;
          sync();
        }, { threshold: 0.25 });
        observer.observe(wrapper);
      } else {
        state.visible = true;
        sync();
      }

      addMediaListener(reduced, sync);
      addMediaListener(mobile, sync);
      document.addEventListener('visibilitychange', sync);
      updateToggle();

      controllers.set(wrapper, () => {
        pauseInternally();
        if (observer) observer.disconnect();
        removeMediaListener(reduced, sync);
        removeMediaListener(mobile, sync);
        document.removeEventListener('visibilitychange', sync);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('ended', updateToggle);
        if (toggle) toggle.removeEventListener('click', onToggle);
      });
    });
  }

  function mount() {
    init();
    document.addEventListener('shopify:section:load', (event) => init(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      controllers.forEach((cleanup, wrapper) => {
        if (event.target.contains(wrapper)) {
          cleanup();
          controllers.delete(wrapper);
        }
      });
    });
  }

  return { shouldPlay, mount };
});
